import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { NAV_GROUPS, SEGMENT_LABELS } from '../../../lib/navigation';
import { Icon } from '../../ui/Icon/Icon';

const ALL_ITEMS = NAV_GROUPS.flatMap((g) => g.items);
interface CrumbState {
  labels: Record<string, string>;
  setLabels: Dispatch<SetStateAction<Record<string, string>>>;
}
const CrumbContext = createContext<CrumbState>({ labels: {}, setLabels: () => {} });

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [labels, setLabels] = useState<Record<string, string>>({});
  const value = useMemo(() => ({ labels, setLabels }), [labels]);
  return <CrumbContext.Provider value={value}>{children}</CrumbContext.Provider>;
}

/**
 * Names a URL part the breadcrumb can't know on its own, e.g.
 * useCrumb(`/customers/${id}`, customer?.name) shows "Customers > Vishal Rasal".
 */
export function useCrumb(path: string, label?: string | null) {
  const { setLabels } = useContext(CrumbContext);
  useEffect(() => {
    if (!label) return;
    setLabels((l) => (l[path] === label ? l : { ...l, [path]: label }));
  }, [path, label, setLabels]);
}

export function Breadcrumbs() {
  const { pathname } = useLocation();
  const { labels } = useContext(CrumbContext);
  const parts = pathname.split('/').filter(Boolean);

  const crumbs =
    parts.length === 0
      ? [{ to: '/', label: 'Dashboard' }]
      : parts.map((seg, i) => {
          const to = `/${parts.slice(0, i + 1).join('/')}`;
          const nav = ALL_ITEMS.find((item) => item.to === to);
          return { to, label: nav?.label ?? labels[to] ?? SEGMENT_LABELS[seg] ?? 'Details' };
        });

  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol className="flex min-w-0 items-center gap-1.5 text-sm">
        <li className="shrink-0">
          <Link to="/" aria-label="Dashboard" className="flex rounded p-0.5 text-ink-400 outline-none hover:text-navy-800 focus-visible:ring-2 focus-visible:ring-steel">
            <Icon name="home" size={17} />
          </Link>
        </li>
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          return (
            <li key={c.to} className={last ? 'flex min-w-0 items-center gap-1.5' : 'hidden shrink-0 items-center gap-1.5 sm:flex'}>
              <Icon name="chevronRight" size={14} className="shrink-0 text-ink-300" />
              {last ? (
                <span aria-current="page" className="truncate font-medium text-navy-900">{c.label}</span>
              ) : (
                <Link to={c.to} className="rounded text-ink-500 outline-none hover:text-navy-900 focus-visible:ring-2 focus-visible:ring-steel">
                  {c.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
